import React, { useState } from 'react';
import axios from 'axios';
import { Send, User, Phone, Mail, MapPin, FileText } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import WhatsAppFallback from './WhatsAppFallback';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuickInquiryForm = ({ variant = 'light' }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', service: '', city: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (window.gtag) window.gtag('event', 'form_submit', { event_category: 'Lead', event_label: 'quick_inquiry_form' });
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: `[Quick Inquiry] Service: ${form.service} | City: ${form.city} | Message: ${form.message}`
      });
      setSubmitted(true);
      toast.success('Inquiry submitted! We will call you within 24 hours.');
    } catch {
      toast.error('Failed to submit. Please call us at 077579 99805.');
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === 'dark';
  const inputCls = isDark
    ? 'bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11 focus:border-[#00AEEF]'
    : 'bg-white border-gray-200 h-11 focus:border-[#00AEEF]';

  if (submitted) {
    return (
      <div className={`rounded-2xl p-8 text-center ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white shadow-xl border border-gray-100'}`} data-testid="inquiry-form-success">
        <div className="w-16 h-16 bg-[#00AEEF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Send className="w-7 h-7 text-[#00AEEF]" />
        </div>
        <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#003366]'}`}>Thank You!</h3>
        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Our pool expert will contact you within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`rounded-2xl p-6 md:p-8 ${isDark ? 'bg-white/5 border border-white/10 backdrop-blur-sm' : 'bg-white shadow-xl border border-gray-100'}`} data-testid="quick-inquiry-form">
      <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-[#003366]'}`}>Request Free Consultation</h3>
      <p className={`text-xs mb-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Get expert advice — we respond within 24 hours</p>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <User className={`absolute left-3 top-3 w-4 h-4 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="Name" className={`pl-9 ${inputCls}`} />
          </div>
          <div className="relative">
            <Phone className={`absolute left-3 top-3 w-4 h-4 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
            <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required type="tel" placeholder="Phone" className={`pl-9 ${inputCls}`} />
          </div>
        </div>
        <div className="relative">
          <Mail className={`absolute left-3 top-3 w-4 h-4 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
          <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} required type="email" placeholder="Email" className={`pl-9 ${inputCls}`} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <FileText className={`absolute left-3 top-3 w-4 h-4 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
            <select
              value={form.service}
              onChange={e => setForm({...form, service: e.target.value})}
              required
              className={`w-full rounded-md pl-9 pr-3 text-sm ${inputCls}`}
            >
              <option value="">Service Required</option>
              <option value="Pool Construction">Pool Construction</option>
              <option value="Pool Renovation">Pool Renovation</option>
              <option value="Pool AMC">Pool AMC & Maintenance</option>
              <option value="Waterproofing">Waterproofing</option>
              <option value="Jacuzzi Installation">Jacuzzi & Sauna</option>
              <option value="Pipeless Filtration">Pipeless Filtration</option>
              <option value="Commercial Pool">Commercial Pool</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="relative">
            <MapPin className={`absolute left-3 top-3 w-4 h-4 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
            <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="City" className={`pl-9 ${inputCls}`} />
          </div>
        </div>
        <textarea
          value={form.message}
          onChange={e => setForm({...form, message: e.target.value})}
          placeholder="Tell us about your project (optional)"
          rows={2}
          className={`w-full rounded-md px-3 py-2 text-sm resize-none ${isDark ? 'bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:border-[#00AEEF]' : 'bg-white border border-gray-200 focus:border-[#00AEEF]'}`}
        />
      </div>
      <Button
        type="submit"
        disabled={loading}
        className="w-full mt-4 bg-[#00AEEF] hover:bg-[#0098d4] text-white font-bold py-5 text-sm rounded-xl shadow-lg shadow-[#00AEEF]/20 hover:scale-[1.02] transition-all"
        data-testid="inquiry-form-submit"
      >
        {loading ? 'Submitting...' : 'Request Free Consultation'}
      </Button>
      <WhatsAppFallback
        prefill={`Hi Aquarist Pools, I'm ${form.name || '[Your Name]'} interested in ${form.service || 'pool services'}. My phone: ${form.phone || '[Phone]'}. City: ${form.city || '[City]'}.`}
        label="Or chat with us on WhatsApp"
      />
      <p className={`text-[10px] mt-2 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No spam. Your information is kept confidential.</p>
    </form>
  );
};

export default QuickInquiryForm;
